import { OrderQuery } from 'app/order/state/order.query';
import { Location } from '@angular/common';
import { AfterViewInit, Component, inject, OnDestroy } from '@angular/core';
import { ActivatedRoute, DefaultUrlSerializer, Router, NavigationEnd, EventType } from '@angular/router';
import { combineLatest, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith, takeUntil } from 'rxjs/operators';
import { StepCloseEvent } from '../../components/step-toggle/step-toggle.component';
import { OrderService } from '../../state/order.service';
import { OrderState } from 'app/order/models/order-state.model';
import { PriceService } from '../../price/price.service';
import { PageMeta, PageMetaService } from '../../../page-meta';
import { ProductsService } from 'app/products/products.service';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { OrderStepsService } from 'app/order/order-step.service';
import { isProduct } from '@models/product';
import { InsuranceTypesService } from '@core';

@Component({
    selector: 'app-order-container-view',
    templateUrl: './order-container-view.component.html',
    styleUrls: ['./order-container-view.component.scss'],
    standalone: false,
})
export class OrderContainerViewComponent implements OnDestroy, AfterViewInit {
    private productService = inject(ProductsService);
    private orderStepsService = inject(OrderStepsService);

    private unsubscribe$: Subject<void> = new Subject<void>();

    protected price = this.priceService.price;
    protected product = this.productService.product;
    private product$ = toObservable(this.product);

    protected insuranceType = inject(InsuranceTypesService).insuranceType;

    protected state = toSignal(this.query.state$);

    protected isLandingPage = this.orderStepsService.isLandingPage;

    public constructor(
        public readonly query: OrderQuery,
        public readonly priceService: PriceService,
        private readonly route: ActivatedRoute,
        private readonly router: Router,
        private readonly orderService: OrderService,
        private readonly location: Location,
        private readonly pageMetaService: PageMetaService,
    ) {
        this.subscribeToRouteDataChange();
        this.subscribeForRouterNavigationEndChange();
    }

    public ngAfterViewInit(): void {
        this.subscribeForStepChange();
        this.subscribeForLocationChange();
        this.subscribeForProductMeta();
    }

    public ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    protected prev(state: Partial<OrderState> = {}): void {
        this.orderService.prev(state);
    }

    protected next(state: Partial<OrderState> = {}): void {
        const isFinalStep = this.orderStepsService.isFinalStep();
        if (isFinalStep) {
            this.submit(state);
        } else {
            this.orderService.next(state);
        }
    }

    protected onClose(event: StepCloseEvent): void {
        this.orderService.update(event.state);
        if (event.step !== '') {
            this.orderService.setValidState(event.step, event.isInvalid);
        }
    }

    protected isComplete(slug: string): boolean {
        return this.orderStepsService.isStepComplete(slug);
    }

    protected isPreviousStepComplete(slug: string): boolean {
        return this.orderStepsService.isPreviousStepComplete(slug);
    }

    protected toggle(step: string): void {
        if (step !== this.orderStepsService.currentStep()) {
            this.orderStepsService.setStep(step);
        } else {
            this.orderStepsService.setStep(this.orderStepsService.getLatestStep());
        }
    }

    private submit(state: Partial<OrderState> = {}): void {
        this.orderService.submit(state).subscribe(() => {
            this.router.navigate(['/', 'produkte', 'bestellung-abgeschlossen']);
        });
    }

    // #region subscriptions
    /**
     * set correct url and scroll to correct step
     */
    private subscribeForStepChange(): void {
        this.query.currentStep$.pipe(distinctUntilChanged(), takeUntil(this.unsubscribe$)).subscribe((step) => {
            this.scrollToStep(step);
            const url = this.urlFromStep(step);
            if (!this.location.isCurrentPathEqualTo(url)) {
                this.location.go(url);
            }
        });
    }

    /**
     * whenever we get a NavigationEnd we want to make sure
     * we show the correct state - this might happen by e.g.
     * clicking in the header on the same insurance type
     */
    private subscribeForRouterNavigationEndChange(): void {
        this.router.events
            .pipe(
                filter((event) => event.type === EventType.NavigationEnd),
                takeUntil(this.unsubscribe$),
            )
            .subscribe((event: NavigationEnd) => {
                this.orderStepsService.setStep(this.stepFromUrl(event.url));
            });
    }

    private subscribeForLocationChange(): void {
        // route.params does not update when using the back button to
        // switch between to steps repeatedly. The back button will
        // trigger a popState event though we can use to update current
        // step. Location allows us to subscribe to these events.
        const locationSubscription = this.location.subscribe((value) => {
            const step = this.stepFromUrl(value.url);
            this.orderStepsService.setStep(step);
        });
        // location is no real observable, so we can't use .pipe.
        // But it returns a SubscriptionLike we can use to unsubscribe.
        this.unsubscribe$.subscribe(() => locationSubscription.unsubscribe());
    }

    /**
     * set the product
     * this needs to be called ASAP
     */
    public subscribeToRouteDataChange(): void {
        this.route.data.pipe(takeUntil(this.unsubscribe$)).subscribe((data) => {
            const product = data.productData;
            if (!isProduct(product)) {
                throw new Error('Malformed product data');
            }
            this.orderService.setProduct(product);

            if (typeof data.customer !== 'undefined') {
                this.orderService.update({
                    ...data.customer,
                });
            }
        });
    }

    /**
     * set page meta when product updates or navigation end events are triggered
     */
    private subscribeForProductMeta(): void {
        const navigationEndObservable: Observable<null> = this.router.events.pipe(
            filter((e) => e.type === EventType.NavigationEnd),
            map(() => null),
            startWith(null),
        );

        combineLatest([this.product$, navigationEndObservable])
            .pipe(
                map(([product]) => product?.presentation.meta ?? {}),
                takeUntil(this.unsubscribe$),
            )
            .subscribe((meta: PageMeta) => this.pageMetaService.setPageMeta(meta));
    }
    // #endregion

    // #region url conversion
    private urlFromStep(step: string): string {
        const urlSerializer = new DefaultUrlSerializer();
        const urlTree = this.router.createUrlTree(['..', step], {
            relativeTo: this.route,
        });
        return urlSerializer.serialize(urlTree);
    }

    private stepFromUrl(url: string): string {
        const urlSerializer = new DefaultUrlSerializer();
        const urlTree = this.router.createUrlTree(['..'], {
            relativeTo: this.route,
        });
        const baseUrl = urlSerializer.serialize(urlTree) + '/';
        return url.split('?')[0].slice(baseUrl.length);
    }
    // #endregion

    private scrollToStep(step: string): void {
        setTimeout(() => {
            const el = document.getElementById(`toggle-${step}`);
            if (el) {
                setTimeout(() => {
                    const header = document.getElementById('page-header');
                    const rect = el.getBoundingClientRect();
                    window.scrollTo({
                        top: window.scrollY + rect.top - (header?.clientHeight ?? 0) - 12,
                        behavior: 'smooth',
                    });
                }, 10);
            } else {
                window.scrollTo(0, 0);
            }
        });
    }
}
